import clsx from 'clsx'
import { useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'

import { ValidatedForm } from '@fv/client-components'
import { useFormModelState } from '@fv/client-core'
import { displayWorkflow } from '@fv/client-types'
import type { LoadTemplateDTO, Workflow } from '@fv/models'

import { FooterBar } from '@/components/FooterBar'
import { FormActions } from '@/components/forms/FormActions'
import InfoBox from '@/components/InfoBox'
import { InputGroup, InputGroupWrapper } from '@/components/inputs/InputGroup'
import { PageHeader } from '@/components/PageHeader'

import { BillToForm } from '../../billTo/BillToForm'
import { defaultFormBillTo } from '../../billTo/billToFuncs'
import { BookingFormTags } from '../../book/BookingFormTags'
import { LoadItemsForm } from '../../commodities/load-items/LoadItemsFormProvider'
import { defaultLoadHandlingUnit } from '../../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../../commodities/load-items/types'
import { BrandingFormSection } from '../../company-branding/BrandingSelector'
import { GLCodeSelector } from '../../gl-codes/GLCodeSelector'
import { useDefaultWorkflow, usePreferredWorkflows } from '../../profiile/hooks'
import { ShipmentEquipmentForm } from '../../shipment-equipment-form/ShipmentEquipmentForm'
import { ShipmentFormSection } from '../../shipment-form/ShipmentFormSection'
import { ShipmentLocationForm } from '../../shipment-location-form/ShipmentLocationForm'
import {
  defaultDestination,
  defaultOrigin,
} from '../../shipment-location-form/types'
import { useAccountSettings } from '../account-settings/hooks'
import { useUpsertLoadTemplate } from './mutations'
import type { ShipmentTemplateFormModel } from './types'
import {
  emptyTemplateEquipment,
  emptyTemplateFormModel,
  formModelToPayload,
  templateToFormModel,
} from './utils'

// keeps all collapsible section pills on this panel the same width, without affecting other pages
const pillWidthClassName = 'w-44'

type Props = {
  existingTemplate?: LoadTemplateDTO
  draftTemplate?: LoadTemplateDTO
  initialWorkflow?: Workflow
  onSaved?: (template: LoadTemplateDTO) => void
  onCancel?: () => void
}

export const ShipmentTemplatePanel = ({
  existingTemplate,
  draftTemplate,
  initialWorkflow,
  onSaved,
  onCancel,
}: Props) => {
  const defaultWorkflow = useDefaultWorkflow()
  const preferredWorkflows = usePreferredWorkflows()
  const accountSettings = useAccountSettings()
  const upsertTemplate = useUpsertLoadTemplate(existingTemplate?._id)
  const isEditing = Boolean(existingTemplate)
  const initialTemplate = existingTemplate ?? draftTemplate

  const workflowOptions = useMemo(
    () =>
      preferredWorkflows.map(workflow => ({
        text: displayWorkflow(workflow),
        value: workflow,
      })),
    [preferredWorkflows],
  )

  const {
    register,
    value: model,
    setValue,
    updateValue: setModel,
  } = useFormModelState<ShipmentTemplateFormModel>({
    initialValue: initialTemplate
      ? templateToFormModel(initialTemplate)
      : emptyTemplateFormModel(initialWorkflow ?? defaultWorkflow),
  })

  const showBillTo = model.workflow !== 'parcel'

  const handleWorkflowChange = useCallback(
    (workflow: Workflow) => {
      setModel({
        workflow,
        origin: defaultOrigin(),
        destination: defaultDestination(),
        billTo: { ...defaultFormBillTo },
        equipment: emptyTemplateEquipment(),
        items: [defaultLoadHandlingUnit(workflow)],
      })
    },
    [setModel],
  )

  const handleItemsChange = useCallback(
    (items: LoadHandlingUnitFormModel[]) => setModel({ items }),
    [setModel],
  )

  const handleSubmit = useCallback(async () => {
    if (!model.name.trim()) {
      toast.error('Template name is required')
      return
    }

    const savedTemplate = await upsertTemplate.mutateAsync(
      formModelToPayload(model),
    )
    onSaved?.(savedTemplate)
  }, [model, onSaved, upsertTemplate])

  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      className="flex flex-col flex-1 overflow-hidden"
    >
      <div className="p-6 flex-1 overflow-auto">
        <PageHeader
          title={isEditing ? 'Edit shipment template' : 'New shipment template'}
          noBorder
        />
        <InfoBox>
          Predefine shipment details for reuse. Expand each section that you
          wish to enter information.
        </InfoBox>
        <div className="flex gap-x-2 mb-12">
          <InputGroup
            inputType="text"
            label="Template name"
            required
            autoFocus
            className="mb-0 flex-1"
            inputProps={{
              ...register('name'),
              placeholder: 'e.g. West Coast outbound',
            }}
          />
          {workflowOptions.length > 0 && (
            <InputGroup
              inputType="select"
              label="Mode"
              className="mb-0 basis-64"
              inputProps={{
                name: 'workflow',
                options: workflowOptions,
                value: model.workflow,
                onChange: e =>
                  handleWorkflowChange(e.currentTarget.value as Workflow),
              }}
            />
          )}
        </div>
        <div className="flex flex-col gap-y-8">
          <BrandingFormSection
            value={model.brandId}
            onChange={brandId => setModel({ brandId })}
            className="border-fv-gray bg-fv-gray-100"
            title="Branding"
            pillClassName={clsx('bg-white border-fv-gray', pillWidthClassName)}
            pillClickable
            collapsible
            defaultCollapsed={false}
          />

          <ShipmentLocationForm
            workflow={model.workflow}
            value={model.origin}
            onChange={origin => setModel({ origin })}
            allowDateEdit={false}
            isTemplate
            collapsible
            defaultCollapsed={true}
            pillClassName={pillWidthClassName}
            pillClickable
          />

          <ShipmentLocationForm
            workflow={model.workflow}
            value={model.destination}
            onChange={destination => setModel({ destination })}
            allowDateEdit={false}
            isTemplate
            collapsible
            defaultCollapsed={true}
            pillClassName={pillWidthClassName}
            pillClickable
          />

          {model.workflow === 'truckload' && (
            <ShipmentEquipmentForm
              isTemplate
              value={model.equipment}
              onChange={equipment => setModel({ equipment })}
              collapsible
              defaultCollapsed={true}
              pillClassName={pillWidthClassName}
              pillClickable
            />
          )}

          <ShipmentFormSection
            title="Handling units"
            className="border-fv-gray bg-fv-gray-100"
            pillClassName={clsx('bg-white border-fv-gray', pillWidthClassName)}
            pillClickable
            collapsible
            defaultCollapsed
          >
            <div className="mt-4">
              <LoadItemsForm
                workflow={model.workflow}
                initialItems={model.items}
                step="booking"
                type="inline"
                onChange={handleItemsChange}
                isTemplate
              />
            </div>
          </ShipmentFormSection>

          {showBillTo && (
            <ShipmentFormSection
              title="Bill to"
              className="border-fv-gray bg-fv-gray-100"
              pillClassName={clsx(
                'bg-white border-fv-gray',
                pillWidthClassName,
              )}
              pillClickable
              collapsible
              defaultCollapsed={true}
            >
              <div className="form-row">
                <BillToForm
                  workflow={model.workflow}
                  values={model.billTo}
                  isTemplate
                  setValues={values =>
                    setValue(prev => ({
                      ...prev,
                      billTo: { ...prev.billTo, ...values },
                    }))
                  }
                />
              </div>
            </ShipmentFormSection>
          )}

          <ShipmentFormSection
            title="Other info"
            className="border-fv-gray bg-fv-gray-100 flex gap-x-2"
            pillClassName={clsx('bg-white border-fv-gray', pillWidthClassName)}
            pillClickable
            collapsible
            defaultCollapsed={true}
          >
            <BookingFormTags
              className="flex-1 b1400:basis-full"
              tags={model.tags}
              onChange={tags => setModel({ tags })}
            />
            {accountSettings?.glCode?.enabled && (
              <InputGroupWrapper
                className="w-1/3"
                label="GL Code"
                name="glCode"
              >
                <GLCodeSelector
                  value={model.glCode}
                  onChange={glCode => setModel({ glCode })}
                />
              </InputGroupWrapper>
            )}
          </ShipmentFormSection>
        </div>
      </div>

      <FooterBar>
        <FormActions
          onCancel={onCancel}
          loading={upsertTemplate.isPending}
          submitText="Save"
        />
      </FooterBar>
    </ValidatedForm>
  )
}
